<template>
    <div class="filter-builder">
        <div v-if="!localConditions.filterGroups || localConditions.filterGroups.length === 0" class="empty-filters">
            <div class="empty-icon">
                <i class="material-icons">filter_list_off</i>
            </div>
            <p>{{ $t("taskModuleRules.noConditionsAddedYet") }}</p>
            <a-button type="primary" class="modern-add-button" @click="addFilterGroup">
                <span class="button-content">
                    <i class="material-icons">add</i>
                    <span class="button-text">{{ $t("taskModuleRules.addFirstCondition") }}</span>
                </span>
            </a-button>
        </div>

        <div v-else class="filter-body">
            <!-- Filter Groups -->
            <div
                v-for="(group, groupIndex) in localConditions.filterGroups"
                :key="`group-${groupIndex}`"
                class="filter-group-container"
            >
                <div class="filter-group">
                    <!-- Group Header -->
                    <div v-if="localConditions.filterGroups.length > 1" class="group-header">
                        <span class="group-title">{{
                            $t("taskModuleRules.filterGroupNumbered", { n: groupIndex + 1 })
                        }}</span>
                        <a-button
                            type="text"
                            icon="delete"
                            size="small"
                            danger
                            @click="removeGroup(groupIndex)"
                        >
                            {{ $t("taskModuleRules.deleteFilterGroup") }}
                        </a-button>
                    </div>

                    <!-- Filters in Group -->
                    <div
                        v-for="(filter, filterIndex) in group.filters"
                        :key="`filter-${filterIndex}`"
                        class="filter-row"
                    >
                        <span class="filter-text">
                            <!-- Prefix -->
                            <span v-if="filterIndex === 0" class="prefix">{{ $t("taskModuleRules.filterPrefixIf") }}</span>
                            <InlineSelectOperator
                                v-else
                                v-model="group.operator"
                                :options="[
                                    { label: $t('taskModuleRules.and'), value: 'AND' },
                                    { label: $t('taskModuleRules.or'), value: 'OR' }
                                ]"
                                class="prefix-operator"
                                @change="emitChange"
                            />

                            <!-- Field -->
                            <InlineSelect
                                v-model="filter.field"
                                :options="getAvailableFields()"
                                :placeholder="$t('taskModuleRules.selectField')"
                                :searchable="true"
                                @change="onFieldChange(filter)"
                            />

                            <!-- Operator -->
                            <InlineSelectOperator
                                v-if="filter.field"
                                v-model="filter.operator"
                                :options="getOperatorsForField(filter.field)"
                                :placeholder="$t('taskModuleRules.operatorPlaceholderInline')"
                                @change="emitChange"
                            />

                            <!-- Value -->
                            <template v-if="filter.field && filter.operator && needsValue(filter.operator)">
                                <!-- For array/multi-select -->
                                <span v-if="isMultiSelect(filter)" class="multi-value-wrapper">
                                    <a-select
                                        v-model="filter.value"
                                        mode="multiple"
                                        class="multi-select-inline"
                                        :placeholder="$t('taskModuleRules.select')"
                                        @change="emitChange"
                                    >
                                        <a-select-option 
                                            v-for="opt in getFieldOptions(filter.field)" 
                                            :key="opt.value" 
                                            :value="opt.value"
                                        >
                                            {{ opt.label }}
                                        </a-select-option>
                                    </a-select>
                                </span>

                                <!-- For single-select with options -->
                                <InlineSelectValue
                                    v-else-if="hasOptions(filter.field)"
                                    v-model="filter.value"
                                    :options="getFieldOptions(filter.field)"
                                    :placeholder="$t('taskModuleRules.selectValue')"
                                    :searchable="true"
                                    @change="emitChange"
                                />

                                <!-- Boolean -->
                                <InlineSelectValue
                                    v-else-if="getFieldDataType(filter.field) === 'boolean'"
                                    v-model="filter.value"
                                    :options="[
                                        { label: $t('taskModuleRules.yes'), value: true },
                                        { label: $t('taskModuleRules.no'), value: false }
                                    ]"
                                    :placeholder="$t('taskModuleRules.selectOptionPlaceholder')"
                                    @change="emitChange"
                                />

                                <!-- Date -->
                                <a-date-picker
                                    v-else-if="getFieldDataType(filter.field) === 'date'"
                                    v-model="filter.value"
                                    :placeholder="$t('taskModuleRules.selectDatePlaceholder')"
                                    format="DD/MM/YYYY"
                                    @change="emitChange"
                                />

                                <!-- Regular text/number input -->
                                <InlineInput
                                    v-else
                                    v-model="filter.value"
                                    :type="getFieldDataType(filter.field) === 'number' ? 'number' : 'text'"
                                    :placeholder="$t('taskModuleRules.enterValuePlaceholder')"
                                    @change="emitChange"
                                />
                            </template>
                        </span>

                        <!-- Remove Button -->
                        <a-button
                            type="text"
                            size="small"
                            class="remove-btn"
                            @click="removeFilter(groupIndex, filterIndex)"
                        >
                            <i class="material-icons">delete_outline</i>
                        </a-button>
                    </div>

                    <!-- Add Filter Button -->
                    <div class="add-condition-btn" @click="addFilterToGroup(groupIndex)">
                        <i class="material-icons">add</i>
                        <span>{{ $t("taskModuleRules.addCondition") }}</span>
                    </div>
                </div>

                <!-- Group Operator -->
                <div
                    v-if="groupIndex < localConditions.filterGroups.length - 1"
                    class="group-operator-row"
                >
                    <div class="separator-line"></div>
                    <div class="operator-badge">
                        <a-select
                            v-model="localConditions.groupOperator"
                            size="small"
                            class="operator-selector"
                            @change="emitChange"
                        >
                            <a-select-option value="AND">VE (AND)</a-select-option>
                            <a-select-option value="OR">VEYA (OR)</a-select-option>
                        </a-select>
                    </div>
                    <div class="separator-line"></div>
                </div>
            </div>

            <!-- Add Group Button -->
            <div class="add-group-btn" @click="addFilterGroup">
                <i class="material-icons">add_circle_outline</i>
                <span>{{ $t("taskModuleRules.addFilterGroup") }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { generateRandomId } from "../../../../../../utils/session";
import InlineSelect from "../../../../Automation/components/InlineSelect.vue";
import InlineSelectOperator from "../../../../Automation/components/InlineSelectOperator.vue";
import InlineSelectValue from "../../../../Automation/components/InlineSelectValue.vue";
import InlineInput from "../../../../Automation/components/InlineInput.vue";
import log2 from "../../../../../../utils/log2";
import _ from "lodash";

const log = log2("TaskFilterBuilder");

export default {
    name: "TaskFilterBuilder",
    components: {
        InlineSelect,
        InlineSelectOperator,
        InlineSelectValue,
        InlineInput
    },
    props: {
        value: {
            type: Object,
            required: true
        },
        whenType: {
            type: String,
            required: true
        },
        workflowId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            localConditions: {
                filterGroups: [],
                groupOperator: "AND"
            }
        };
    },
    computed: {
        ...mapState({
            tags: state => state.tag.tags.data || [],
            users: state => state.user.users.data || [],
            subjects: state => state.subject.subjects.data || [],
            workflows: state => state.tasks.workflows || []
        }),
        currentWorkflow() {
            return this.workflows.find(w => w._id === this.workflowId);
        },
        workflowStages() {
            if (!this.currentWorkflow || !this.currentWorkflow.pipeline) return [];
            // Pipeline direkt stage object'lerini içeriyor
            return this.currentWorkflow.pipeline.map(stage => ({
                label: stage.name,
                value: stage._id
            }));
        },
        taskTags() {
            return this.tags
                .filter(t => t.type === 'task')
                .map(t => ({
                    label: t.name,
                    value: t._id
                }));
        },
        departments() {
            return this.subjects
                .filter(s => !s.isDeleted && s.isActive)
                .map(s => ({
                    label: s.languageContent[this.$i18n.locale].name,
                    value: s._id
                }));
        },
        userOptions() {
            return this.users.map(u => ({
                label: u.name,
                value: u._id
            }));
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && !_.isEqual(newVal, this.localConditions)) {
                    this.localConditions = _.cloneDeep(newVal);
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        emitChange() {
            this.$emit("input", _.cloneDeep(this.localConditions));
        },

        addFilterGroup() {
            const newGroup = {
                id: generateRandomId(),
                operator: "AND",
                filters: [{
                    id: generateRandomId(),
                    field: "",
                    operator: "equals",
                    value: null,
                    dataType: "string"
                }]
            };
            this.localConditions.filterGroups.push(newGroup);
            this.emitChange();
        },

        removeGroup(groupIndex) {
            this.localConditions.filterGroups.splice(groupIndex, 1);
            if (this.localConditions.filterGroups.length === 0) {
                this.localConditions.filterGroups = [];
            }
            this.emitChange();
        },

        addFilterToGroup(groupIndex) {
            const newFilter = {
                id: generateRandomId(),
                field: "",
                operator: "equals",
                value: null,
                dataType: "string"
            };
            this.localConditions.filterGroups[groupIndex].filters.push(newFilter);
            this.emitChange();
        },

        removeFilter(groupIndex, filterIndex) {
            const group = this.localConditions.filterGroups[groupIndex];
            if (group.filters.length === 1 && this.localConditions.filterGroups.length > 1) {
                this.removeGroup(groupIndex);
            } else if (group.filters.length > 1) {
                group.filters.splice(filterIndex, 1);
                this.emitChange();
            }
        },

        onFieldChange(filter) {
            filter.dataType = this.getFieldDataType(filter.field);
            filter.operator = "equals";
            filter.value = null;
            this.emitChange();
        },

        getAvailableFields() {
            const fields = [
                // Task basic fields
                { label: "Görev Başlığı", value: "task.title", dataType: "string", hasOptions: false },
                { label: "Görev Açıklaması", value: "task.description", dataType: "string", hasOptions: false },
                { label: "Görev Önceliği", value: "task.priority", dataType: "string", hasOptions: true },
                { label: "Görev Stage'i", value: "task.pipelineStage", dataType: "string", hasOptions: true },
                { label: "Görev Completed", value: "task.completed", dataType: "boolean", hasOptions: false },
                { label: "Görev Done", value: "task.done", dataType: "boolean", hasOptions: false },
                { label: "Görev Archived", value: "task.archived", dataType: "boolean", hasOptions: false },
                
                // Task owner
                { label: "Görev Sahibi Var mı", value: "task.owner.exists", dataType: "boolean", hasOptions: false },
                { label: "Görev Sahibi", value: "task.owner", dataType: "string", hasOptions: true },
                
                // Task assignees/followers/collaborators
                { label: "Görev Atananları", value: "task.assignees", dataType: "array", hasOptions: true },
                { label: "Görev Takipçileri", value: "task.followers", dataType: "array", hasOptions: true },
                { label: "Görev İşbirlikçileri", value: "task.collaborators", dataType: "array", hasOptions: true },
                
                // Task tags
                { label: "Görev Etiketleri", value: "task.tags", dataType: "array", hasOptions: true },
                
                // Task dates
                { label: "Görev Başlangıç Tarihi", value: "task.startDate", dataType: "date", hasOptions: false },
                { label: "Görev Bitiş Tarihi", value: "task.endDate", dataType: "date", hasOptions: false },
                
                // Task attachments & checklist
                { label: "Görevde Ek Dosya Var mı", value: "task.hasAttachment", dataType: "boolean", hasOptions: false },
                { label: "Checklist İlerleme Yüzdesi", value: "task.checklist.progress", dataType: "number", hasOptions: false },
                
                // Task estimated time
                { label: "Tahmini Süre (dakika)", value: "task.estimatedTime", dataType: "number", hasOptions: false },
                
                // Task created by
                { label: "Görevi Oluşturan Tip", value: "task.createdBy.type", dataType: "string", hasOptions: true },
                { label: "Görevi Oluşturan Kullanıcı", value: "task.createdBy.user", dataType: "string", hasOptions: true },
                
                // Task relations
                { label: "Görev İlişkisi Var mı", value: "task.relation.exists", dataType: "boolean", hasOptions: false },
                { label: "Görev İlişki Tipi", value: "task.relation.type", dataType: "string", hasOptions: true },
                
                // Contact (relation) fields
                { label: "İlişkili Kişinin ID'si", value: "task.relation.visitor._id", dataType: "string", hasOptions: false },
                { label: "İlişkili Kişinin Adı", value: "task.relation.visitor.fullname", dataType: "string", hasOptions: false },
                { label: "İlişkili Kişinin E-postası", value: "task.relation.visitor.email", dataType: "string", hasOptions: false },
                { label: "İlişkili Kişinin Telefonu", value: "task.relation.visitor.phone", dataType: "string", hasOptions: false },
                { label: "İlişkili Kişinin WhatsApp", value: "task.relation.visitor.whatsappNumber", dataType: "string", hasOptions: false },
                { label: "İlişkili Kişinin Platformu", value: "task.relation.visitor.platform", dataType: "string", hasOptions: true },
                { label: "İlişkili Kişinin Sahibi", value: "task.relation.visitor.owner", dataType: "string", hasOptions: true },
                { label: "İlişkili Kişinin Etiketleri", value: "task.relation.visitor.tags", dataType: "array", hasOptions: true },
                
                // Conversation (relation) fields
                { label: "İlişkili Konuşmanın Durumu", value: "task.relation.conversation.status", dataType: "string", hasOptions: true },
                { label: "İlişkili Konuşmanın Departmanı", value: "task.relation.conversation.subject", dataType: "string", hasOptions: true },
                
                // System fields
                { label: "Şu An Saat", value: "system.timezone.hour", dataType: "number", hasOptions: false },
                { label: "Şu An Gün", value: "system.timezone.day", dataType: "number", hasOptions: false }
            ];

            return fields;
        },

        getFieldDataType(field) {
            const found = this.getAvailableFields().find(f => f.value === field);
            return found ? found.dataType : "string";
        },

        hasOptions(field) {
            const found = this.getAvailableFields().find(f => f.value === field);
            return found ? found.hasOptions : false;
        },

        isMultiSelect(filter) {
            return this.getFieldDataType(filter.field) === 'array' && ['in', 'not_in'].includes(filter.operator);
        },

        getOperatorsForField(field) {
            const dataType = this.getFieldDataType(field);

            if (dataType === "string") {
                return [
                    { label: "eşittir", value: "equals" },
                    { label: "eşit değildir", value: "not_equals" },
                    { label: "içerir", value: "contains" },
                    { label: "içermez", value: "not_contains" },
                    { label: "var", value: "exists" },
                    { label: "yok", value: "not_exists" }
                ];
            }

            if (dataType === "number") {
                return [
                    { label: "eşittir", value: "equals" },
                    { label: "büyüktür", value: "gt" },
                    { label: "büyük veya eşittir", value: "gte" },
                    { label: "küçüktür", value: "lt" },
                    { label: "küçük veya eşittir", value: "lte" },
                    { label: "arasında", value: "between" }
                ];
            }

            if (dataType === "boolean") {
                return [
                    { label: "eşittir", value: "equals" }
                ];
            }

            if (dataType === "array") {
                return [
                    { label: "içerir", value: "in" },
                    { label: "içermez", value: "not_in" }
                ];
            }

            if (dataType === "date") {
                return [
                    { label: "eşittir", value: "equals" },
                    { label: "sonra", value: "gt" },
                    { label: "sonra veya eşit", value: "gte" },
                    { label: "önce", value: "lt" },
                    { label: "önce veya eşit", value: "lte" }
                ];
            }

            return [];
        },

        needsValue(operator) {
            return !["exists", "not_exists"].includes(operator);
        },

        getFieldOptions(field) {
            // Priority options
            if (field === "task.priority") {
                return [
                    { label: "En Düşük", value: "lowest" },
                    { label: "Düşük", value: "low" },
                    { label: "Orta", value: "medium" },
                    { label: "Yüksek", value: "high" },
                    { label: "En Yüksek", value: "highest" }
                ];
            }

            // Stage options
            if (field === "task.pipelineStage") {
                return this.workflowStages;
            }

            // Owner/Assignees/Followers/Collaborators options
            if (field === "task.owner" || field === "task.assignees" || 
                field === "task.followers" || field === "task.collaborators" ||
                field === "task.createdBy.user" || field === "task.relation.visitor.owner") {
                return this.userOptions;
            }

            // Tags options
            if (field === "task.tags") {
                return this.taskTags;
            }

            // Contact tags options
            if (field === "task.relation.visitor.tags") {
                return this.tags
                    .filter(t => t.type === 'contact')
                    .map(t => ({
                        label: t.name,
                        value: t._id
                    }));
            }

            // Created by type
            if (field === "task.createdBy.type") {
                return [
                    { label: "Kullanıcı", value: "user" },
                    { label: "API", value: "api" },
                    { label: "Chatbot", value: "chatbot" },
                    { label: "Otomasyon", value: "automation" }
                ];
            }

            // Relation type
            if (field === "task.relation.type") {
                return [
                    { label: "Kişi", value: "visitor" },
                    { label: "Konuşma", value: "conversation" },
                    { label: "Mesaj", value: "message" },
                    { label: "Mail", value: "mail" },
                    { label: "Tablo Kaydı", value: "tableRecord" }
                ];
            }

            // Platform options
            if (field === "task.relation.visitor.platform") {
                return [
                    { label: "WhatsApp", value: "whatsapp" },
                    { label: "Instagram", value: "instagram" },
                    { label: "Telegram", value: "telegram" },
                    { label: "Messenger", value: "messenger" },
                    { label: "LinkedIn", value: "linkedin" },
                    { label: "Web", value: "web" },
                    { label: "Mobile", value: "mobile" },
                    { label: "Email", value: "email" },
                    { label: "Trendyol", value: "trendyol" }
                ];
            }

            // Conversation status
            if (field === "task.relation.conversation.status") {
                return [
                    { label: "Chatbot", value: "chatbot" },
                    { label: "Yeni", value: "new" },
                    { label: "Aktif", value: "active" },
                    { label: "Kayıp", value: "lost" },
                    { label: "Sonlandırılmış", value: "destroyed" },
                    { label: "Özel", value: "private" }
                ];
            }

            // Conversation/Visitor department
            if (field === "task.relation.conversation.subject" || field === "task.relation.visitor.preferredDepartment") {
                return this.departments;
            }

            return [];
        },
    }
};
</script>

<style lang="scss" scoped>
.filter-builder {
    .empty-filters {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        background: #fafafa;
        border: 2px dashed #d9d9d9;
        border-radius: 12px;

        .empty-icon {
            .material-icons {
                font-size: 64px;
                color: #d9d9d9;
            }
        }

        p {
            margin: 16px 0 24px;
            color: #999;
            font-size: 14px;
        }

        .modern-add-button {
            height: auto;
            padding: 0;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
            }

            &:active {
                transform: translateY(0);
            }

            .button-content {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 10px 20px;

                .material-icons {
                    font-size: 20px;
                    line-height: 1;
                    display: flex;
                    align-items: center;
                }

                .button-text {
                    line-height: 1;
                    display: flex;
                    align-items: center;
                }
            }
        }
    }

    .filter-body {
        .filter-group-container {
            margin-bottom: 24px;

            .filter-group {
                background: #fafafa;
                border: 1px solid #e8e8e8;
                border-radius: 8px;
                padding: 16px;

                .group-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                    padding-bottom: 12px;
                    border-bottom: 1px solid #e0e0e0;

                    .group-title {
                        font-weight: 600;
                        color: #1a1a1a;
                    }
                }

                .filter-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 8px;
                    padding: 8px;
                    background: #ffffff;
                    border-radius: 6px;

                    .filter-text {
                        flex: 1;
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: 8px;

                        .prefix {
                            font-weight: 600;
                            color: #1890ff;
                        }

                        .multi-value-wrapper {
                            min-width: 200px;
                        }
                    }

                    .remove-btn {
                        .material-icons {
                            font-size: 18px;
                        }
                    }
                }

                .add-condition-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 8px;
                    margin-top: 8px;
                    background: #ffffff;
                    border: 1px dashed #d9d9d9;
                    border-radius: 6px;
                    cursor: pointer;
                    color: #1890ff;
                    transition: all 0.3s ease;

                    &:hover {
                        background: #e6f7ff;
                        border-color: #1890ff;
                    }

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }

            .group-operator-row {
                display: flex;
                align-items: center;
                gap: 16px;
                margin: 16px 0;

                .separator-line {
                    flex: 1;
                    height: 1px;
                    background: #d9d9d9;
                }

                .operator-badge {
                    .operator-selector {
                        min-width: 120px;
                    }
                }
            }
        }

        .add-group-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 12px;
            background: #ffffff;
            border: 2px dashed #1890ff;
            border-radius: 8px;
            cursor: pointer;
            color: #1890ff;
            font-weight: 600;
            transition: all 0.3s ease;

            &:hover {
                background: #e6f7ff;
            }

            .material-icons {
                font-size: 20px;
            }
        }
    }
}
</style>
